import HistoryForm, {
  HistoryFormValues,
} from "Components/Admin/AdminOtherDetails/AdminHistory/HistoryForm";
import HistoryTable from "Components/Admin/AdminOtherDetails/AdminHistory/HistoryTable";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useBulkDeleteHistoryMutation,
  useDeleteHistoryMutation,
  useGetAllHistoryQuery,
} from "Services/HistoryApiSlice";
import type { AdminHistory as AdminHistoryType } from "Types/Admin/AdminHistoryType";

import {
  useGetAllHistoryCoverQuery,
  useSaveHistoryCoverMutation,
  useUpdateHistoryCoverMutation,
  useDeleteHistoryCoverMutation,
  useDeleteHistoryCoverImageMutation,
} from "Services/HistoryCoverApiSlice";

import { Center, Loader, Modal, Input } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Search } from "lucide-react";

const AdminHistory: React.FC = () => {
  const navigate = useNavigate();
  const plant = process.env.REACT_APP_PLANT ?? "";
  const [search, setSearch] = useState("");

  const [isCoverModalOpen, setCoverModalOpen] = useState(false);

  const {
    data: items = [],
    isLoading,
    isError,
    refetch,
  } = useGetAllHistoryQuery({
    plant,
    search: search || undefined,
  });

  const [deleteHistory, { isLoading: isDeleting }] = useDeleteHistoryMutation();

  const [bulkDeleteHistory, { isLoading: isBulkDeleting }] =
    useBulkDeleteHistoryMutation();

  const [saveHistoryCover, { isLoading: isSavingCover }] =
    useSaveHistoryCoverMutation();

  const [updateHistoryCover] = useUpdateHistoryCoverMutation();

  const [deleteHistoryCover, { isLoading: isDeletingCover }] =
    useDeleteHistoryCoverMutation();

  const [deleteHistoryCoverImage, { isLoading: isDeletingCoverImage }] =
    useDeleteHistoryCoverImageMutation();

  const { data: covers = [], refetch: refetchCovers } =
    useGetAllHistoryCoverQuery({ plant });

  const existingCover = covers[0];

  const handleCoverSubmit = async (
    values: HistoryFormValues,
    file: File | null,
  ) => {
    try {
      let response: any;
      if (existingCover?.id) {
        const payload: any = {
          data: {
            id: existingCover.id,
            plant,
            title: values.title,
            description1: values.description1,
            description2: values.description2,
            description3: values.description3,
          },
        };

        if (file) payload.file = file;

        response = await updateHistoryCover(payload).unwrap();
      } else {
        const payload: any = {
          data: {
            plant,
            title: values.title,
            description1: values.description1,
            description2: values.description2,
            description3: values.description3,
          },
        };

        if (file) payload.file = file;

        response = await saveHistoryCover(payload).unwrap();
      }

      notifications.show({
        message: response?.message,
        color: "green",
      });

      await refetchCovers();
      setCoverModalOpen(false);
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteCoverImage = async () => {
    if (!existingCover?.id) return;

    try {
      const response: any = await deleteHistoryCoverImage({ id: existingCover.id, plant }).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      await refetchCovers();
    } catch (error: any) {
      console.error("Failed to delete history cover image:", error);
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteCover = async () => {
    if (!existingCover?.id) return;

    try {
      const response: any = await deleteHistoryCover({ id: existingCover.id, plant }).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      await refetchCovers();
      setCoverModalOpen(false);
    } catch (error: any) {
      console.error("Failed to delete history cover:", error);
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleBulkDelete = async (ids: number[]) => {
    try {
      const response: any = await bulkDeleteHistory({ ids, plant }).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      refetch();
    } catch (err: any) {
      console.error("Bulk delete error", err);

      notifications.show({
        message: err?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <Center style={{ height: 400 }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (isError) {
    return <p style={{ color: "red" }}>Failed to load history.</p>;
  }

  return (
    <div className="px-4 sm:px-8 py-6 pb-24 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 max-w-6xl mx-auto">
        <h3 className="text-base font-medium">History</h3>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Input
            placeholder="Search by history title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftSection={<Search size={16} />}
            className="w-full sm:w-[300px]"
          />

          <div className="flex gap-2">
            <button
              onClick={() => setCoverModalOpen(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded shadow hover:bg-blue-700 whitespace-nowrap"
            >
              Add Cover Image
            </button>

            <button
              onClick={() => navigate("/admin/other-details/history/add")}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded shadow hover:bg-blue-700 whitespace-nowrap"
              disabled={isDeleting || isBulkDeleting}
            >
              + Add History
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-5 rounded shadow-sm w-full max-w-6xl mx-auto min-h-[260px]">
        <HistoryTable
          items={items}
          onView={(item) =>
            navigate(`/admin/other-details/history/view/${item.id}`)
          }
          onEdit={(item: AdminHistoryType) =>
            navigate(`/admin/other-details/history/edit/${item.id}`, {
              state: { item },
            })
          }
          onDelete={async (id) => {
            try {
              const response: any = await deleteHistory({ id, plant }).unwrap();
              notifications.show({
                message: response?.message,
                color: "green",
              });
            } catch (error: any) {
              notifications.show({
                message: error?.data?.message || "An error occurred",
                color: "red",
              });
            }
          }}
          onBulkDelete={handleBulkDelete}
          showBulkActions={true}
          onAddCover={() => setCoverModalOpen(true)}
        />
      </div>

      <Modal
        opened={isCoverModalOpen}
        onClose={() => setCoverModalOpen(false)}
        title="Add History Cover Image"
        size="lg"
        centered
      >
        <HistoryForm
          key={`history-cover-${existingCover?.id ?? "new"}-${
            existingCover?.coverImageUrl ?? "none"
          }`}
          mode="cover"
          loading={isSavingCover}
          initialValues={{
            year: undefined,
            title: existingCover?.title || "",
            description1: existingCover?.description1 || "",
            description2: existingCover?.description2 || "",
            description3: existingCover?.description3 || "",
            imageUrl: existingCover?.coverImageUrl || null,
          }}
          onSubmit={handleCoverSubmit}
          onCancel={() => setCoverModalOpen(false)}
          onDeleteCover={existingCover?.id ? handleDeleteCover : undefined}
          onDeleteImage={existingCover?.coverImageUrl ? handleDeleteCoverImage : undefined}
          isDeletingCoverImage={isDeletingCoverImage}
          isDeletingCover={isDeletingCover}
        />
      </Modal>
    </div>
  );
};

export default AdminHistory;
